import apiClient from './api';

class AuthService {
  /**
   * Admin Login Step 1 - Credential verification + optional captcha
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @param {string} turnstileToken - Cloudflare Turnstile token (optional based on config)
   * @returns {Promise} - Response with either tokens (OTP disabled) or session_id (OTP enabled)
   */
  async loginStep1(email, password, turnstileToken) {
    try {
      console.log('🔐 Attempting login step 1 for:', email);
      const payload = {
        email,
        password,
      };

      if (turnstileToken) {
        payload.turnstile_token = turnstileToken;
      }

      const response = await apiClient.post('/admin/auth/login/step1/', payload);

      console.log('📦 Step 1 API Response:', response.data);

      // Check if OTP is required
      if (response.data.otp_required) {
        // OTP is enabled - return session_id for step 2
        return {
          success: true,
          otpRequired: true,
          data: {
            session_id: response.data.session_id,
            message: response.data.message || 'OTP sent to your email',
          },
        };
      } else {
        // OTP is disabled - tokens returned immediately
        const { access, refresh, admin } = response.data;
        const adminData = this.processAdminData(admin, access, email);
        this.storeAuthData(access, refresh, adminData);

        return {
          success: true,
          otpRequired: false,
          data: {
            access,
            refresh,
            admin: adminData,
          },
        };
      }
    } catch (error) {
      console.error('❌ Login step 1 error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Admin Login Step 2 - OTP verification
   * @param {string} sessionId - Session ID from step 1
   * @param {string} otp - 6-digit OTP code
   * @returns {Promise} - Response with tokens and admin data
   */
  async loginStep2(sessionId, otp) {
    try {
      console.log('🔐 Attempting login step 2 with OTP');
      const response = await apiClient.post('/admin/auth/login/step2/', {
        session_id: sessionId,
        otp,
      });

      console.log('📦 Step 2 API Response:', response.data);

      const { access, refresh, admin } = response.data;
      const adminData = this.processAdminData(admin, access);
      this.storeAuthData(access, refresh, adminData);

      return {
        success: true,
        data: {
          access,
          refresh,
          admin: adminData,
        },
      };
    } catch (error) {
      console.error('❌ Login step 2 error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Process admin data from API response or decode from JWT
   * @param {Object} admin - Admin object from API
   * @param {string} accessToken - Access token to decode if admin not provided
   * @param {string} email - Fallback email
   * @returns {Object} - Processed admin data
   */
  processAdminData(admin, accessToken, email = '') {
    if (admin) {
      console.log('👤 Admin data from API:', admin);
      return admin;
    }

    // Decode JWT to get admin info
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      console.log('🔓 Decoded JWT payload:', payload);
      return {
        email: payload.email || email,
        admin_id: payload.admin_id || payload.user_id,
        role: payload.role || 'admin',
        permissions: payload.permissions || {},
      };
    } catch (decodeError) {
      console.warn('⚠️ Could not decode JWT, using basic admin data');
      return {
        email: email,
        role: 'admin',
      };
    }
  }

  /**
   * Store authentication data in localStorage
   * @param {string} access - Access token
   * @param {string} refresh - Refresh token
   * @param {Object} adminData - Admin data object
   */
  storeAuthData(access, refresh, adminData) {
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem('adminData', JSON.stringify(adminData));
    localStorage.setItem('isAuthenticated', 'true');

    console.log('💾 Stored in localStorage:');
    console.log('  accessToken:', localStorage.getItem('accessToken') ? 'STORED' : 'FAILED');
    console.log('  refreshToken:', localStorage.getItem('refreshToken') ? 'STORED' : 'FAILED');
    console.log('  adminData:', localStorage.getItem('adminData') ? 'STORED' : 'FAILED');
    console.log('  isAuthenticated:', localStorage.getItem('isAuthenticated'));
  }

  /**
   * Admin Logout
   * @returns {Promise} - Logout response
   */
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        await apiClient.post('/admin/auth/logout/', {
          refresh: refreshToken,
        });
      }

      // Clear all stored data
      this.clearAuthData();

      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (error) {
      // Clear data even if API call fails
      this.clearAuthData();
      return this.handleError(error);
    }
  }

  /**
   * Refresh Access Token
   * @returns {Promise} - New tokens
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post('/admin/auth/refresh/', {
        refresh: refreshToken,
      });

      const { access, refresh } = response.data;

      // Update tokens
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);

      return {
        success: true,
        data: {
          access,
          refresh,
        },
      };
    } catch (error) {
      // If refresh fails, clear auth data
      this.clearAuthData();
      return this.handleError(error);
    }
  }

  /**
   * Verify Token
   * @param {string} token - Token to verify
   * @returns {Promise} - Verification result
   */
  async verifyToken(token) {
    try {
      await apiClient.post('/admin/auth/verify/', {
        token,
      });

      return {
        success: true,
        message: 'Token is valid',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get Current Admin Data
   * @returns {Object|null} - Admin data from localStorage
   */
  getCurrentAdmin() {
    try {
      const adminData = localStorage.getItem('adminData');
      if (!adminData || adminData === 'undefined' || adminData === 'null') {
        return null;
      }
      return JSON.parse(adminData);
    } catch (error) {
      console.error('Error parsing admin data:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} - Authentication status
   */
  isAuthenticated() {
    const accessToken = localStorage.getItem('accessToken');
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    return !!(accessToken && isAuth);
  }

  /**
   * Get Access Token
   * @returns {string|null} - Access token
   */
  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  /**
   * Get Refresh Token
   * @returns {string|null} - Refresh token
   */
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // ==================== FORGOT PASSWORD METHODS ====================

  /**
   * Forgot Password - Step 1: Initiate
   * @param {string} emailOrPhone - Admin email or phone
   * @returns {Promise} - Response with session_token
   */
  async forgotPasswordInitiate(emailOrPhone) {
    try {
      console.log('🔐 Initiating forgot password for:', emailOrPhone);
      const response = await apiClient.post('/admin/auth/forgot-password/initiate/', {
        email_or_phone: emailOrPhone,
      });

      console.log('📦 Initiate Response:', response.data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Forgot password initiate error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Forgot Password - Step 2: Send OTP
   * @param {string} sessionToken - Session token from initiate step
   * @param {string} contactMethod - Contact method ('email' or 'phone')
   * @returns {Promise} - Response confirming OTP sent
   */
  async forgotPasswordSendOtp(sessionToken, contactMethod) {
    try {
      console.log('📧 Sending OTP via:', contactMethod);
      const response = await apiClient.post('/admin/auth/forgot-password/send-otp/', {
        session_token: sessionToken,
        contact_method: contactMethod,
      });

      console.log('📦 Send OTP Response:', response.data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Forgot Password - Step 3: Verify OTP
   * @param {string} sessionToken - Session token from initiate step
   * @param {string} otpCode - 6-digit OTP code
   * @returns {Promise} - Response confirming OTP verified
   */
  async forgotPasswordVerifyOtp(sessionToken, otpCode) {
    try {
      console.log('🔑 Verifying OTP');
      const response = await apiClient.post('/admin/auth/forgot-password/verify-otp/', {
        session_token: sessionToken,
        otp_code: otpCode,
      });

      console.log('📦 Verify OTP Response:', response.data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Forgot Password - Step 4: Reset Password
   * @param {string} sessionToken - Session token from initiate step
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise} - Response confirming password reset
   */
  async forgotPasswordReset(sessionToken, newPassword, confirmPassword) {
    try {
      console.log('🔒 Resetting password');
      const response = await apiClient.post('/admin/auth/forgot-password/reset/', {
        session_token: sessionToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      console.log('📦 Reset Password Response:', response.data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Reset password error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('adminData');
    localStorage.removeItem('isAuthenticated');
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';
    let statusCode = null;

    if (error.response) {
      // Server responded with error
      statusCode = error.response.status;

      if (error.response.data) {
        // Extract error message from response
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          } else {
            message = JSON.stringify(error.response.data);
          }
        }
      }

      // Handle specific status codes
      if (statusCode === 401) {
        message = 'Invalid email or password';
      } else if (statusCode === 403) {
        message = 'Access denied. You do not have permission.';
      } else if (statusCode === 429) {
        message = 'Too many requests. Please try again later.';
      } else if (statusCode >= 500) {
        message = 'Server error. Please try again later.';
      }
    } else if (error.request) {
      // Request made but no response
      message = 'Unable to connect to server. Please check your internet connection.';
    } else {
      // Error in request setup
      message = error.message || message;
    }

    return {
      success: false,
      error: message,
      statusCode,
    };
  }
}

export default new AuthService();
